
import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import StandardHeader from './StandardHeader';
import BottomTabNavigation from './BottomTabNavigation';
import { TrialBanner } from '@/components/TrialBanner';
import { useTranslation } from 'react-i18next';

interface AppLayoutProps {
  children: ReactNode;
  headerTitle?: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  hideBottomNav?: boolean;
  hideHeader?: boolean;
}

const AppLayout = ({ 
  children, 
  headerTitle, 
  showBackButton = false, 
  onBackClick,
  hideBottomNav = false,
  hideHeader = false
}: AppLayoutProps) => {
  const location = useLocation();
  const { t } = useTranslation();


  // Determine if we should show bottom navigation based on route
  const shouldShowBottomNav = !hideBottomNav && (
    location.pathname === '/feed' ||
    location.pathname === '/my-pets' ||
    location.pathname === '/create-post' ||
    location.pathname === '/notifications' ||
    location.pathname === '/profile' ||
    location.pathname === '/matches' ||
    location.pathname.startsWith('/profile/') ||
    location.pathname.startsWith('/pet/') ||
    location.pathname.startsWith('/edit-pet/') ||
    location.pathname.startsWith('/posts/') ||
    location.pathname.startsWith('/services') ||
    location.pathname.startsWith('/provider-dashboard') ||
    location.pathname.startsWith('/register-provider')
  );

  // Determine if we should show back button based on route
  const shouldShowBackButton = showBackButton || (
    location.pathname.startsWith('/pet/') ||
    location.pathname.startsWith('/edit-pet/') ||
    location.pathname === '/create-post' ||
    location.pathname.startsWith('/medical/') ||
    location.pathname.startsWith('/posts/') ||
    location.pathname.startsWith('/profile/') ||
    location.pathname === '/subscription' ||
    location.pathname === '/settings' ||
    location.pathname === '/matches'
  );

  // Generate contextual header titles (i18n-aware)
  const getContextualTitle = () => {
    if (headerTitle) return headerTitle;
    
    const path = location.pathname;
    if (path === '/feed') return t('titles.feed');
    if (path === '/my-pets') return t('titles.myPets');
    if (path === '/create-post') return t('titles.createMemory');
    if (path === '/notifications') return t('titles.notifications');
    if (path === '/profile') return t('titles.profile');
    if (path.startsWith('/pet/')) return t('titles.petProfile');
    if (path.startsWith('/edit-pet/')) return t('titles.editPet');
    if (path === '/settings') return t('titles.settings');
    if (path === '/subscription') return t('titles.subscription');
    if (path.startsWith('/profile/')) return t('titles.profile');
    if (path.startsWith('/medical/')) {
      if (path.includes('/vaccinations/new')) return t('titles.medicalAddVaccination');
      if (path.includes('/vaccinations/edit/')) return t('titles.medicalEditVaccination');
      if (path.includes('/vaccinations')) return t('titles.medicalVaccinations');
      if (path.includes('/appointments/new')) return t('titles.medicalScheduleAppointment');
      if (path.includes('/appointments/edit/')) return t('titles.medicalEditAppointment');
      if (path.includes('/appointments')) return t('titles.medicalAppointments');
      if (path.includes('/records/new')) return t('titles.medicalAddRecord');
      if (path.includes('/records/edit/')) return t('titles.medicalEditRecord');
      if (path.includes('/records')) return t('titles.medical');
      return t('titles.medical');
    }
    
    return t('titles.appName');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col safe-area-x">
      <TrialBanner />
      {!hideHeader && (
        <StandardHeader 
          title={getContextualTitle()}
          showBackButton={shouldShowBackButton}
          onBackClick={onBackClick}
        />
      )}
      
      <main className={`flex-1 ${shouldShowBottomNav ? 'pb-16' : ''}`}>
        {children}
      </main>
      
      {shouldShowBottomNav && <BottomTabNavigation />}
    </div>
  );
};

export default AppLayout;
